/* global Excel */
import store from "../../store/store";
import { clearSheetDiffs, removeDiffRow, upsertDiffRow } from "../../slices/diffSlice";
import { Product, DiffSyncResult } from "./types";
import { hasRowDiff, buildMagentoPayload, normalizeHeader, mapHeaderToProductKey } from "./utils";

export const DIFF_COLOR = "#FFF3B0";
export const BLOCK_COLOR = "#E0115F";
export const DEFAULT_PAGE_SIZE = Number(process.env.SYNC_PAGE_SIZE) || 100;

export async function highlightDiffsOnSheet(sheetId: string, products: Product[]) {
  if (!products || products.length === 0) return;

  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetId);
      sheet.load("name");
      await context.sync();

      if (sheet.isNullObject) return;

      const usedRange = sheet.getUsedRangeOrNullObject(true);
      usedRange.load(["rowCount", "columnCount"]);
      await context.sync();

      if (usedRange.isNullObject || usedRange.rowCount < 2 || usedRange.columnCount === 0) return;

      const headerRange = sheet.getRangeByIndexes(0, 0, 1, usedRange.columnCount);
      headerRange.load("values");
      await context.sync();

      const headers = headerRange.values[0] as string[];
      const dataRange = sheet.getRangeByIndexes(1, 0, usedRange.rowCount - 1, usedRange.columnCount);
      dataRange.load("values");
      await context.sync();

      store.dispatch(clearSheetDiffs(sheetId));

      const rows = dataRange.values as any[];

      rows.forEach((rowValues, rowOffset) => {
        const rowIndex = rowOffset + 1;
        const rowRange = sheet.getRangeByIndexes(rowIndex, 0, 1, usedRange.columnCount);
        const hasDiffResult = hasRowDiff(headers, rowValues, products);
        rowRange.format.fill.color = hasDiffResult ? DIFF_COLOR : "#FFFFFF";

        if (hasDiffResult) {
          store.dispatch(
            upsertDiffRow({
              sheetId,
              rowIndex,
              data: buildMagentoPayload(headers, rowValues),
            })
          );
        }
      });
    });
  } catch (err) {
    console.error("highlightDiffsOnSheet error:", err);
  }
}

export async function insertProductsToSheet(sheetId: string, products: Product[]) {
  if (!products || products.length === 0) {
    console.warn("No products to insert.");
    return;
  }

  const headers = ["SKU", "Name", "Type", "Price", "Quantity"];
  const rows = products.map((product) => [
    product.sku ?? "",
    product.name ?? "",
    product.type_id ?? "",
    product.price ?? "",
    product.quantity ?? "",
  ]);

  const data = [headers, ...rows];

  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetId);
      sheet.load("id");
      await context.sync();

      if (sheet.isNullObject) return;

      const range = sheet.getRange(`A1:E${data.length}`);
      range.values = data;
      range.format.fill.color = "#FFFFFF";

      const usedRange = sheet.getUsedRangeOrNullObject(true);
      usedRange.load(["rowCount"]);
      await context.sync();

      if (!usedRange.isNullObject && usedRange.rowCount > data.length) {
        const extraRowCount = usedRange.rowCount - data.length;
        const extraRange = sheet.getRangeByIndexes(data.length, 0, extraRowCount, 5);
        extraRange.clear(Excel.ClearApplyTo.contents);
        extraRange.format.fill.color = "#FFFFFF";
      }

      range.format.autofitColumns();
      await context.sync();
    });
  } catch (error) {
    console.log("Error inserting products: " + error);
  }
}

export async function ensureWorksheetForPage(page: number) {
  let sheetId = "";
  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    const sheetName = `Page ${page}`;
    const sheet = worksheets.getItemOrNullObject(sheetName);
    sheet.load(["id", "name"]);
    await context.sync();

    if (sheet.isNullObject) {
      const created = worksheets.add(sheetName);
      created.load(["id"]);
      await context.sync();
      sheetId = created.id;
      return;
    }

    sheetId = sheet.id;
  });

  return sheetId;
}

export async function insertProducts(products: Product[]) {
  try {
    let sheetId = "";
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.load("id");
      await context.sync();
      sheetId = sheet.id;
    });
    if (sheetId) {
      await insertProductsToSheet(sheetId, products);
    }
  } catch (error) {
    console.log("Error inserting products: " + error);
  }
}

export async function applySyncResults(sheetId: string, results: DiffSyncResult[]) {
  if (!results.length) return;

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItemOrNullObject(sheetId);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) return;

    const usedRange = sheet.getUsedRangeOrNullObject(true);
    usedRange.load(["rowCount", "columnCount"]);
    await context.sync();

    if (usedRange.isNullObject || usedRange.rowCount === 0) return;

    const headerRange = sheet.getRangeByIndexes(0, 0, 1, usedRange.columnCount);
    headerRange.load("values");
    await context.sync();

    const headers = headerRange.values[0] as string[];
    const skuHeaderIndex = headers.findIndex((header) => normalizeHeader(header) === "sku");

    for (const result of results) {
      if (result.rowIndex === 0) continue;
      const rowRange = sheet.getRangeByIndexes(result.rowIndex, 0, 1, usedRange.columnCount);
      rowRange.format.fill.color = "#FFFFFF";

      if (skuHeaderIndex !== -1 && result.sku) {
        const skuCell = sheet.getRangeByIndexes(result.rowIndex, skuHeaderIndex, 1, 1);
        skuCell.values = [[result.sku]];
      }
    }

    await context.sync();
  });

  results.forEach((result) => {
    store.dispatch(removeDiffRow({ sheetId, rowIndex: result.rowIndex }));
  });
}

export async function applyPullResults(sheetId: string, results: DiffSyncResult[]) {
  if (!results.length) return;

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItemOrNullObject(sheetId);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) return;

    const usedRange = sheet.getUsedRangeOrNullObject(true);
    usedRange.load(["rowCount", "columnCount"]);
    await context.sync();

    if (usedRange.isNullObject || usedRange.rowCount === 0) return;

    const headerRange = sheet.getRangeByIndexes(0, 0, 1, usedRange.columnCount);
    headerRange.load("values");
    await context.sync();

    const headers = headerRange.values[0] as string[];

    for (const result of results) {
      if (result.rowIndex === 0 || !result.data) continue;
      
      const newRowValues = headers.map((header) => {
        const key = normalizeHeader(header);
        if (!key) return "";
        const productKey = mapHeaderToProductKey(key);
        const val = result.data[productKey];
        return val !== undefined && val !== null ? val : "";
      });

      const rowRange = sheet.getRangeByIndexes(result.rowIndex, 0, 1, usedRange.columnCount);
      rowRange.values = [newRowValues];
      rowRange.format.fill.color = "#FFFFFF";
    }

    await context.sync();
  });

  results.forEach((result) => {
    store.dispatch(removeDiffRow({ sheetId, rowIndex: result.rowIndex }));
  });
}
